// Core packages
import { formatDate } from "@angular/common";
import { Component, Inject } from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";

// Custom packages
import { PrivacyDocsListDialogComponent } from "src/app/modules/properties/components/privacy-docs-list-dialog/privacy-docs-list-dialog.component";
import { HelperService } from "src/app/shared/services/helper.service";
import {
  DEFAULT_PRIVACY_DOC_EXTENSION,
  EMPLOYEE_PRIVACY_TEMPLATE,
  PRIVACY_DOC_MIME_EXTENSIONS,
  PrivacyDocsService,
} from "src/app/shared/services/privacy-docs.service";

interface IAdminCompanyPrivacySagDialogData {
  propertyId: string;
  companyId: string;
  /** Whether the property has at least one non-deleted lease contract document */
  hasLeaseContracts: boolean;
  /** Whether the property's condo structure has at least one caretaker/employee */
  hasCaretaker: boolean;
}

/**
 * Shown after saving property-detail2's form 1 when the admin company has
 * just been changed to one with an active "Privacy SAG" subscription: it
 * prompts the user to (re)generate the mandatory privacy documents, and
 * offers a direct download of the caretaker document when applicable.
 *
 * @since 1.0.0
 */
@Component({
  selector: "app-admin-company-privacy-sag-dialog",
  templateUrl: "./admin-company-privacy-sag-dialog.component.html",
  styleUrls: ["./admin-company-privacy-sag-dialog.component.scss"]
})
export class AdminCompanyPrivacySagDialogComponent {
  caretakerDocDownloading = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<AdminCompanyPrivacySagDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAdminCompanyPrivacySagDialogData,
    private dialog: MatDialog,
    private privacyDocsService: PrivacyDocsService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
  ) { }

  /**
   * Close this dialog and open the full privacy documents list dialog
   * (property, supplier, tenant, employee, data processor appointment), so
   * the user can generate/regenerate whichever mandatory document applies.
   *
   * @since 1.0.0
   */
  onGenerateDocuments(): void {
    this.dialogRef.close();
    this.dialog.open(PrivacyDocsListDialogComponent, {
      data: {
        propertyId: this.data.propertyId,
        companyId: this.data.companyId,
      },
      width: "650px",
      disableClose: true,
    });
  }

  /**
   * Generate and download the employee privacy document, to be re-signed by
   * the property's caretaker.
   *
   * @since 1.0.0
   */
  onDownloadCaretakerDoc(): void {
    if (this.caretakerDocDownloading || !this.data || !this.data.companyId || !this.data.propertyId) {
      return;
    }

    this.caretakerDocDownloading = true;
    this.privacyDocsService
      .generateDocuments(this.data.companyId, [this.data.propertyId], EMPLOYEE_PRIVACY_TEMPLATE)
      .subscribe(
        (fileBlob: Blob) => {
          this.caretakerDocDownloading = false;
          const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
          const fileNamePrefix = this.translateService.instant(
            "properties.home.privacyDocsDialog.exportFileName.employee"
          );
          const extension = PRIVACY_DOC_MIME_EXTENSIONS[fileBlob.type] || DEFAULT_PRIVACY_DOC_EXTENSION;
          FileSaver.saveAs(fileBlob, `${fileNamePrefix}-${date}.${extension}`);
          this.toastrService.success(
            this.translateService.instant("properties.home.privacyDocsDialog.success")
          );
        },
        (err) => {
          this.caretakerDocDownloading = false;
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle "Chiudi": close the dialog
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
